import { useEffect, useMemo, useState } from 'react';
import { matchPath, useLocation, useNavigate } from 'react-router-dom';
import { Button, Collapse } from 'reactstrap';

export interface MenuItem {
  id?: string;
  label: string;
  icon?: string;
  link?: string;
  isHeader?: boolean;
  subItems?: MenuItem[];
  relatedRoutes?: string[];
}

interface NavMenuItemProps {
  item: MenuItem;
}

const NavMenuItem = ({ item }: NavMenuItemProps) => {
  const navigate = useNavigate();
  const location = useLocation();

  const [open, setOpen] = useState(false);

  const relevantPaths = useMemo(() => {
    const extractSubLinks = (menuItem: MenuItem): string[] => {
      const links: string[] = [];

      if (menuItem.subItems) {
        menuItem.subItems.forEach((subItem: MenuItem) => {
          if (subItem.link) {
            links.push(subItem.link);
          }

          if (subItem.relatedRoutes) {
            links.push(...subItem.relatedRoutes);
          }

          if (subItem.subItems) {
            links.push(...extractSubLinks(subItem));
          }
        });
      }

      return links;
    };

    return [...extractSubLinks(item), ...(item.relatedRoutes ?? []), ...(item.link ? [item.link] : [])];
  }, [item]);

  const isActive = useMemo(() => {
    return relevantPaths.some((link) => matchPath(link, location.pathname));
  }, [relevantPaths, location.pathname]);

  useEffect(() => {
    if (isActive) {
      setOpen(true);
    }
  }, [isActive]);

  const toggleOpen = () => {
    setOpen(!open);
  };

  const onMenuItemClick = () => {
    if (hasChildren) {
      toggleOpen();
    }

    if (item.link) {
      navigate(item.link);
    }
  };

  if (item.isHeader) {
    return (
      <li className="menu-title">
        <span data-key="t-menu">{item.label} </span>
      </li>
    );
  }

  const hasChildren = item?.subItems && (item?.subItems?.length ?? 0) > 0;

  // prettier-ignore
  const dropdownMenu = hasChildren && (
    <Collapse isOpen={open} className={`menu-dropdown${open ? ' show' : ''}`}>
      <ul className="nav nav-sm flex-column visible flex-nowrap">
        {item.subItems?.map((subItem: MenuItem, key: number) => (
          <NavMenuItem key={key} item={subItem} />
        ))}
      </ul>
    </Collapse>
  );

  return (
    <li className="nav-item">
      <Button
        color="link"
        className={`w-100 nav-link menu-link d-flex align-items-center justify-content-between ${isActive || open ? 'active' : ''}`}
        onClick={onMenuItemClick}
        aria-expanded={hasChildren ? open : undefined}
        title={item.label}
      >
        <span
          className="d-flex align-items-center justify-content-start flex-grow-1 overflow-hidden"
          style={{ minWidth: 0 }}
        >
          {item.icon && <i className={`${item.icon} me-2`}></i>}
          <span data-key="t-apps" className="d-block text-truncate">
            {item.label}
          </span>
        </span>
        {hasChildren && <i className={open ? 'ri-arrow-down-s-line' : 'ri-arrow-up-s-line'}></i>}
      </Button>
      {dropdownMenu}
    </li>
  );
};

export default NavMenuItem;
